import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  Affix,
  Divider,
  Form,
  Input,
  PageHeader,
  Popconfirm,
  Select,
  Switch,
} from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxInputImage } from 'components';
import { ArrowLeftIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import { RevxSpin } from 'components/RevxSpin';
import { Headline } from 'components/RevxTypography';
import {
  BUSINESS_TYPE_COLLECTION,
  SkuGalleryLabelCreateParams,
  SkuGalleryLabelUpdateParams,
} from 'domains/gallerySkuDomain';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ADMIN_DETAIL_AFFIX_TOP } from 'utils/constants';
import { RULE_TEXT_LEN_255 } from 'utils/validators';

const GalleryLabelItemForm = ({ isCompany }: { isCompany: boolean }) => {
  const { t } = useTranslation();
  const formItemLayout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 10 },
  };

  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const saving = useAppSelector(
    (state) =>
      state.loading.effects.galleryLabel.updateItem ||
      state.loading.effects.galleryLabel.createItem
  );
  const { item, data } = useAppSelector((state) => state.galleryLabel);

  const parentLabels = useMemo(
    () =>
      data?.galleryLabels
        .map((labelWithCount) => labelWithCount.skuLabel)
        .filter((label) => !label.parentId) || [],
    [data]
  );

  const doSave = async (
    values: SkuGalleryLabelUpdateParams | SkuGalleryLabelCreateParams
  ) => {
    const updated = Object.assign({}, item, values, { isCompany });
    if (updated.id === undefined) {
      await dispatch.galleryLabel.createItem(updated);
    } else {
      await dispatch.galleryLabel.updateItem(updated);
    }
    onClose();
  };

  const onSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (validationError) {
      // do nothing when form invalid
      return;
    }

    doSave(values);
  };

  const onClose = () => {
    dispatch.galleryLabel.setState({ item: undefined });
  };
  const onSaveAsNew = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (validationError) {
      // do nothing when form invalid
      return;
    }
    const updated = Object.assign({}, item, values);
    updated.id = undefined;
    await dispatch.galleryLabel.createItem(updated);
    onClose();
  };

  const onRemove = async () => {
    if (item?.id) {
      await dispatch.galleryLabel.removeItem({ ...item, isCompany });
      onClose();
    }
  };

  const actionBar = (
    <div>
      <Affix offsetTop={ADMIN_DETAIL_AFFIX_TOP}>
        <PageHeader
          className="platform-item-form-header"
          onBack={() => {
            dispatch.galleryLabel.setState({ item: undefined });
          }}
          backIcon={<ArrowLeftIcon />}
          title={
            <Headline>
              {item?.id
                ? t('platform.resources.galleryLabels.edit')
                : t('platform.resources.galleryLabels.create')}
            </Headline>
          }
          extra={[
            <RevxButton type="primary" brandGreen onClick={onSave}>
              {t('common.save')}
            </RevxButton>,
            ...(item?.id
              ? [
                  <RevxButton onClick={onSaveAsNew} type="primary" brandGreen>
                    {t('common.saveAsNew')}
                  </RevxButton>,
                ]
              : []),
            <>
              <Divider type="vertical" />
              <RevxButton onClick={onClose} brandGreen>
                {t('common.cancel')}
              </RevxButton>
            </>,
            ...(item?.id
              ? [
                  <>
                    <Divider type="vertical" />
                    <Popconfirm
                      title={t('common.confirmQuestion')}
                      icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                      onConfirm={onRemove}
                      okText={t('common.ok')}
                      cancelText={t('common.cancel')}
                    >
                      <RevxButton danger>{t('common.remove')}</RevxButton>
                    </Popconfirm>
                  </>,
                ]
              : []),
          ]}
        ></PageHeader>
      </Affix>
    </div>
  );

  return (
    <RevxSpin spinning={saving}>
      {actionBar}
      <div className="platform-item-form-wrapper">
        <Form
          {...formItemLayout}
          layout={'horizontal'}
          form={form}
          className="platform-item-form"
          initialValues={{
            ...item,
          }}
          onValuesChange={(values) => console.log(values)}
        >
          <Form.Item
            label={t('common.active')}
            name="active"
            valuePropName="checked"
          >
            <Switch disabled={!Boolean(item?.id)} />
          </Form.Item>

          <Form.Item
            label={t('platform.resources.galleryLabels.businessTypes')}
            name="businessTypes"
            hidden={isCompany}
          >
            <Select
              getPopupContainer={(e) => {
                return e as HTMLElement;
              }}
              mode="multiple"
            >
              {BUSINESS_TYPE_COLLECTION.map((bt) => (
                <Select.Option value={bt.id}>{bt.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t('platform.resources.galleryLabels.dropdownOf')}
            name="parentId"
            hidden={isCompany}
          >
            <Select
              getPopupContainer={(e) => {
                return e as HTMLElement;
              }}
              allowClear
            >
              {parentLabels
                ?.filter((l) => l.id !== item?.id)
                .map((l) => (
                  <Select.Option key={l.id} value={l.id}>
                    {l.name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t('common.name')}
            name="name"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('common.description')}
            name="description"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.resources.galleryLabels.summary')}
            name="summary"
            rules={[RULE_TEXT_LEN_255]}
            hidden={isCompany}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={t('platform.resources.galleryLabels.image')}
            name="image"
          >
            <RevxInputImage />
          </Form.Item>
        </Form>
      </div>
    </RevxSpin>
  );
};
export default GalleryLabelItemForm;
